type CompletionHandler = (response: Response) => void;

var csrfToken = null;
var completionHandlers: Array<CompletionHandler> = [];

type ErrorDetails = {
  detail?: string;
  errors?: Record<string, string>;
};

type RequestState = {
  submitting: boolean;
  error: string | null;
  errors: Record<string, string>;
  errorData: object | null;
  info?: string | null;
};

class HttpError extends Error {
  response: Response | null;
  state: RequestState;
  data: object | null;

  constructor(
    response: Response | null,
    body: string,
    data: ErrorDetails | null,
  ) {
    const message = data ? data?.detail : body;
    super(message);
    this.name = "HttpError";
    this.response = response;
    this.state = {
      submitting: false,
      error: this.message,
      errors: data?.errors || {},
      errorData: data,
    };
  }
}

function setCsrfToken(token) {
  csrfToken = token || null;
}

function onComplete(handler: CompletionHandler) {
  completionHandlers.push(handler);
  return function () {
    completionHandlers = completionHandlers.filter(function (item) {
      return item !== handler;
    });
  };
}

function addParams(url, params) {
  if (!params) return url;
  var parsed = new URL(url, window.location.href);
  Object.keys(params).forEach(function (key) {
    var values = Array.isArray(params[key]) ? params[key] : [params[key]];
    values.forEach(function (value) {
      if (value !== undefined && value !== null) {
        parsed.searchParams.append(key, value);
      }
    });
  });
  return parsed.toString();
}

function encodeForm(data) {
  var form = new URLSearchParams();
  Object.keys(data || {}).forEach(function (key) {
    var values = Array.isArray(data[key]) ? data[key] : [data[key]];
    values.forEach(function (value) {
      if (value !== undefined && value !== null) {
        form.append(key, value);
      }
    });
  });
  return form;
}

async function request(url, options = {}) {
  var method = (options.method || "GET").toUpperCase();
  var headers = new Headers(options.headers || {});
  var requestUrl = addParams(url, options.query);
  var body;

  headers.set("Accept", "application/json");
  if (
    new URL(requestUrl, window.location.href).origin === window.location.origin
  ) {
    headers.set("X-Requested-With", "XMLHttpRequest");
  }
  if (method !== "GET" && method !== "HEAD" && csrfToken) {
    headers.set("X-CSRF-Token", csrfToken);
  }
  if (options.json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(options.json);
  } else if (options.form !== undefined) {
    body = encodeForm(options.form);
  }

  var response;
  try {
    response = await fetch(requestUrl, {
      method: method,
      headers: headers,
      body: body,
      credentials: options.credentials || "same-origin",
      signal: options.signal,
    });
    var text = await response.text();
    var data = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch (error) {
        throw new HttpError(response, text, null);
      }
    }
    if (!response.ok) {
      throw new HttpError(response, text, data);
    }
    return data;
  } catch (error) {
    if (error instanceof HttpError) {
      throw error;
    } else {
      throw new HttpError(null, error.message, null);
    }
  } finally {
    completionHandlers.slice().forEach(function (handler) {
      handler(response);
    });
  }
}

function getJson(url, options = {}) {
  return request(url, options);
}

function postJson(url, data = {}, options = {}) {
  return request(
    url,
    Object.assign({}, options, { method: "POST", json: data }),
  );
}

function postForm(url, data = {}, options = {}) {
  return request(
    url,
    Object.assign({}, options, { method: "POST", form: data }),
  );
}

export {
  RequestState,
  HttpError,
  getJson,
  onComplete,
  postForm,
  postJson,
  request,
  setCsrfToken,
};
